'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';

import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '@/modules/common/lib/utils';
import { isSafariBrowser } from '@/modules/common/services/utils';

const DrawerContext = React.createContext({
  onOpenChange: (_value: boolean) => {},
  open: false,
});

const Drawer = ({
  shouldScaleBackground = true,
  repositionInputs,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => {
  const [open, setOpen] = useControllableState({
    prop: props.open,
    defaultProp: false,
    onChange: props.onOpenChange,
  });

  const isIOS =
    typeof window !== 'undefined' &&
    (isSafariBrowser() || /iPhone|iPad|iPod/i.test(navigator.userAgent));

  /**
   * Workaround for drawer to make sure the body styles are reset on unmount
   */
  React.useEffect(() => {
    return () => {
      if (typeof window === 'undefined') {
        return;
      }

      document.body.style.position = '';
      document.body.style.height = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.top = '';
      document.body.style.bottom = '';
      document.body.style.pointerEvents = '';
    };
  }, []);

  return (
    <DrawerContext.Provider
      value={React.useMemo(() => ({ onOpenChange: setOpen, open: open ?? false }), [open, setOpen])}
    >
      <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        /**
         * (felipea 03/23/2026) repositionInputs break the drawer in android devices, but neeed for iOS devices otherwise
         * the mobile keyboard will overflow the drawer content and some fields will be inacessible.
         */
        repositionInputs={repositionInputs ?? isIOS}
        {...props}
      />
    </DrawerContext.Provider>
  );
};
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black !opacity-50', className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    useRoundedTop?: boolean;
    useOpenOverlay?: boolean;
  }
>(({ className, children, useRoundedTop = true, useOpenOverlay = true, ...props }, ref) => {
  const context = React.useContext(DrawerContext);

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <>
      {useOpenOverlay &&
        createPortal(
          <div
            className={cn(
              'fixed inset-0 z-50 bg-black transition-opacity duration-300',
              context.open ? 'opacity-50' : 'pointer-events-none opacity-0'
            )}
          />,
          document.body
        )}
      <DrawerPortal>
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 mt-24 flex h-full max-h-[96%] flex-col rounded-t-[10px] border bg-background lg:rounded-none [&[data-vaul-drawer]]:!will-change-auto',
            className
          )}
          onPointerDownOutside={() => context.onOpenChange(false)}
          /* Fix for consistently allowing mobile vertical scrolling */
          onTouchMove={(e) => {
            e.stopPropagation();
            return;
          }}
          {...props}
        >
          {useRoundedTop && (
            <div className="w-full rounded-t-md bg-white">
              <div className="mx-auto mt-2 h-1 w-8 rounded-sm bg-gray-200 lg:hidden" />
            </div>
          )}
          {children}
        </DrawerPrimitive.Content>
      </DrawerPortal>
    </>
  );
});
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
